// Canvas kéo thả cho sơ đồ NHIỀU MẠNG — PHẦN THUẦN (không DOM, không mạng): hình học, tự dàn theo làn/đường, kiểm nối hợp lệ, chèn ô vào giữa cạnh, xoá ô tự nối lại, trần 60 ô / 6 đường,
// hoàn tác / làm lại, nhãn mạng, nhãn đọc màn hình. Phần giao diện ở pages/so-do-canvas.ts chỉ gọi các hàm này rồi vẽ lại. Mọi hàm KHÔNG ném (đầu vào xấu → từ chối kèm lý do tiếng Việt) và
// KHÔNG sinh NaN/Infinity.
//
// Mô hình dữ liệu (src/shared/do-thi.ts): các ĐƯỜNG RỜI NHAU — mỗi ô có tối đa MỘT ô trước và MỘT ô sau, không vòng. Bố cục (x, y) nằm NGOÀI sơ đồ (BoCuc: id ô → vị trí) vì là chuyện giao diện.
import { GIOI_HAN_DO_THI, GIOI_HAN_LAP, type CanhDoThi, type CanhLap, type NutDoThi } from "../../../src/shared/do-thi";

// ================= HẰNG SỐ HÌNH HỌC =================
export const RONG_O = 176;
export const CAO_O = 64;
/** Khe tối thiểu giữa hai ô khi chèn một ô vào giữa. */
export const KHE_CHEN = 40;
/** Bước ngang giữa hai ô liền nhau trong một đường khi tự dàn (rộng ô + 80). */
export const BUOC_NGANG = RONG_O + 80;
/** Bước dọc giữa hai đường (làn) khi tự dàn — chừa chỗ cho nhãn "xen kẽ" phía trên cạnh và "điểm nhường" dưới ô Nghỉ. */
export const KHOANG_DUONG = 170;
export const LE_TRAI = 40;
export const LE_TREN = 40;
/** Cách tối thiểu giữa hai ô khi tìm chỗ trống cho ô mới. */
const LE_TRONG = 16;
export const K_MIN = 0.15;
export const K_MAX = 2.5;

export interface ViTri { x: number; y: number }
export type BoCuc = Record<string, ViTri>;
export interface View { k: number; tx: number; ty: number }
/** `lap` (07/10/2026) = các cạnh LẶP "Quay lại" (không thuộc `canh`: đường vẫn có ô đầu, ô cuối). Thiếu = không có vòng. */
export interface TrangThai { nut: NutDoThi[]; canh: CanhDoThi[]; boCuc: BoCuc; lap?: CanhLap[] }
export type KetQuaKiem = { ok: true } | { ok: false; ma: string; lyDo: string };
export interface CanhBaoNut { muc: "vang" | "do"; loi: string }

const hh = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const so = (v: unknown, mac = 0): number => (hh(v) ? v : mac);
const laId = (v: unknown): v is string => typeof v === "string" && v.length > 0;
const viTriHopLe = (p: unknown): p is ViTri => !!p && typeof p === "object" && hh((p as ViTri).x) && hh((p as ViTri).y);
const sao = <T>(v: T): T => structuredClone(v);

// ================= CẠNH =================
/** Không khai `canh` = một chuỗi theo thứ tự nut[] (cùng quy ước với do-thi.ts). */
export function canhMacDinh(nut: readonly Pick<NutDoThi, "id">[]): CanhDoThi[] {
  return nut.slice(1).map((n, i) => ({ tu: nut[i]!.id, den: n.id }));
}

/** Chuẩn hoá cạnh do bên ngoài đưa vào: bỏ cạnh trỏ ô lạ, tự nối, trùng, ô vào/ra > 1, tạo vòng (giữ cạnh hợp lệ ĐẦU TIÊN). `boQua` = số cạnh bị bỏ. Không ném. */
export function chuanHoaCanh(nut: readonly Pick<NutDoThi, "id">[], canh: readonly CanhDoThi[] | undefined): { canh: CanhDoThi[]; boQua: number } {
  if (canh === undefined) return { canh: canhMacDinh(nut), boQua: 0 };
  const co = new Set(nut.map((n) => n.id));
  const tiep = new Map<string, string>(), truoc = new Map<string, string>();
  const ra: CanhDoThi[] = [];
  let boQua = 0;
  for (const c of Array.isArray(canh) ? canh : []) {
    const tu = (c as CanhDoThi | null)?.tu, den = (c as CanhDoThi | null)?.den;
    if (!laId(tu) || !laId(den) || !co.has(tu) || !co.has(den) || tu === den || tiep.has(tu) || truoc.has(den)) { boQua++; continue; }
    // vòng: đi từ `den` theo cạnh mà gặp `tu`
    let vong = false;
    for (let cur: string | undefined = den, dem = 0; cur !== undefined && dem <= nut.length; cur = tiep.get(cur), dem++) if (cur === tu) { vong = true; break; }
    if (vong) { boQua++; continue; }
    tiep.set(tu, den); truoc.set(den, tu);
    ra.push({ tu, den });
  }
  return { canh: ra, boQua };
}

/** Các ĐƯỜNG theo thứ tự tạo (đường xếp theo vị trí SỚM NHẤT trong nut[] của ô thuộc đường; trong đường theo CẠNH). Chịu được đồ thị hỏng: ô nằm trong vòng thành đường riêng, không treo. */
export function tachDuong(nut: readonly NutDoThi[], canh: readonly CanhDoThi[]): NutDoThi[][] {
  const chiSo = new Map(nut.map((n, i) => [n.id, i] as const));
  const tiep = new Map<string, string>(), truoc = new Set<string>();
  for (const c of canh) {
    if (!c || !chiSo.has(c.tu) || !chiSo.has(c.den) || c.tu === c.den || tiep.has(c.tu) || truoc.has(c.den)) continue;
    tiep.set(c.tu, c.den); truoc.add(c.den);
  }
  const thay = new Set<string>();
  const duong: { sao: number; ds: NutDoThi[] }[] = [];
  const di = (batDau: NutDoThi): void => {
    const ds: NutDoThi[] = [];
    for (let cur: string | undefined = batDau.id; cur !== undefined && !thay.has(cur); cur = tiep.get(cur)) { thay.add(cur); ds.push(nut[chiSo.get(cur)!]!); }
    if (ds.length) duong.push({ sao: Math.min(...ds.map((x) => chiSo.get(x.id)!)), ds });
  };
  for (const n of nut) if (!truoc.has(n.id) && !thay.has(n.id)) di(n);
  for (const n of nut) if (!thay.has(n.id)) di(n); // còn sót = nằm trong vòng (đồ thị hỏng)
  duong.sort((a, b) => a.sao - b.sao);
  return duong.map((d) => d.ds);
}

export const soDuong = (nut: readonly NutDoThi[], canh: readonly CanhDoThi[]): number => tachDuong(nut, canh).length;

// ================= KIỂM NỐI / TRẦN =================
const tenCuaId = (ten: ((id: string) => string) | undefined, id: string): string => { try { return ten ? ten(id) : id; } catch { return id; } };

/** Có nối được `tu` → `den` không? Từ chối (KHÔNG ném) kèm lý do tiếng Việt: ô lạ, nối chính nó, trùng, ô nguồn đã có ô kế tiếp, ô đích đã có ô trước, tạo vòng. */
export function kiemNoi(tt: Pick<TrangThai, "nut" | "canh">, tu: string, den: string, ten?: (id: string) => string): KetQuaKiem {
  const T = (id: string): string => `“${tenCuaId(ten, id)}”`;
  const co = new Set(tt.nut.map((n) => n.id));
  if (!laId(tu) || !laId(den) || !co.has(tu) || !co.has(den)) return { ok: false, ma: "khong_co_o", lyDo: "Không tìm thấy ô để nối (có thể ô vừa bị xoá)." };
  if (tu === den) return { ok: false, ma: "noi_chinh_no", lyDo: `Không nối ô ${T(tu)} với chính nó.` };
  const ra = tt.canh.find((c) => c.tu === tu);
  const vao = tt.canh.find((c) => c.den === den);
  if (ra && ra.den === den) return { ok: false, ma: "trung", lyDo: `Hai ô ${T(tu)} và ${T(den)} đã được nối rồi.` };
  if (ra) return { ok: false, ma: "da_co_ra", lyDo: `Ô ${T(tu)} đã nối tới ${T(ra.den)} — mỗi ô chỉ nối tới MỘT ô kế tiếp. Muốn thêm việc ở giữa hãy chèn ô vào giữa nối, hoặc gỡ nối cũ trước.` };
  if (vao) return { ok: false, ma: "da_co_vao", lyDo: `Ô ${T(den)} đã nhận từ ${T(vao.tu)} — mỗi ô chỉ nhận từ MỘT ô trước. Muốn gộp hãy làm đường riêng, hoặc gỡ nối cũ trước.` };
  const tiep = new Map(tt.canh.map((c) => [c.tu, c.den] as const));
  for (let cur: string | undefined = den, dem = 0; cur !== undefined && dem <= tt.nut.length; cur = tiep.get(cur), dem++) {
    if (cur === tu) return { ok: false, ma: "tao_vong", lyDo: `Nối ${T(tu)} → ${T(den)} sẽ tạo vòng (ô ${T(den)} đã đi tới ${T(tu)}) — mỗi đường phải có ô đầu và ô cuối.` };
  }
  return { ok: true };
}

/** Thêm ô mới được không? `chen` (vào giữa một nối) và `noi_duoi` (nối đuôi một ô chưa có ô kế) không thêm đường; `co_lap` (ô lẻ) thêm một đường mới. */
export function kiemThemO(tt: Pick<TrangThai, "nut" | "canh">, kieu: "chen" | "noi_duoi" | "co_lap"): KetQuaKiem {
  if (tt.nut.length >= GIOI_HAN_DO_THI.nut) return { ok: false, ma: "tran_o", lyDo: `Đồ thị tối đa ${GIOI_HAN_DO_THI.nut} ô (đang có ${tt.nut.length}). Hãy xoá bớt ô không cần.` };
  if (kieu === "co_lap" && soDuong(tt.nut, tt.canh) >= GIOI_HAN_DO_THI.duong) return { ok: false, ma: "tran_duong", lyDo: `Tối đa ${GIOI_HAN_DO_THI.duong} đường. Hãy nối ô mới vào cuối một đường có sẵn thay vì tạo đường thứ ${GIOI_HAN_DO_THI.duong + 1}.` };
  return { ok: true };
}

/** Gỡ nối thứ `i` tách một đường thành hai → thêm một đường: từ chối khi đã đủ 6 đường. */
export function kiemXoaCanh(tt: Pick<TrangThai, "nut" | "canh">, i: number): KetQuaKiem {
  if (!Number.isInteger(i) || i < 0 || i >= tt.canh.length) return { ok: false, ma: "khong_co_noi", lyDo: "Không tìm thấy nối cần gỡ." };
  if (soDuong(tt.nut, tt.canh) >= GIOI_HAN_DO_THI.duong) return { ok: false, ma: "tran_duong", lyDo: `Gỡ nối này sẽ tách thành đường thứ ${GIOI_HAN_DO_THI.duong + 1} — tối đa ${GIOI_HAN_DO_THI.duong} đường. Hãy nối bớt các đường lại trước.` };
  return { ok: true };
}

// ================= BỐ CỤC =================
/** Hai ô (góc trên-trái) có đè nhau (kể cả chưa đủ `le` px khe) không. */
export function chamNhau(a: ViTri, b: ViTri, le = 0): boolean {
  return !(a.x + RONG_O + le <= b.x || b.x + RONG_O + le <= a.x || a.y + CAO_O + le <= b.y || b.y + CAO_O + le <= a.y);
}

/** Tự dàn: MỖI ĐƯỜNG MỘT LÀN (y theo số thứ tự đường), ô trong đường xếp trái → phải theo thứ tự cạnh. Xác định, không bao giờ trùng ô. */
export function tuDan(nut: readonly NutDoThi[], canh: readonly CanhDoThi[]): BoCuc {
  const bc: BoCuc = {};
  tachDuong(nut, canh).forEach((d, lan) => d.forEach((n, cot) => { bc[n.id] = { x: LE_TRAI + cot * BUOC_NGANG, y: LE_TREN + lan * KHOANG_DUONG }; }));
  return bc;
}

/** Tìm chỗ gần (x, y) nhất mà ô không đè (kể cả khe 16 px) ô nào trong `boCuc` (trừ `boQua`). Đầu vào xấu (NaN…) → dùng góc mặc định. Luôn trả số hữu hạn. */
export function viTriTrong(boCuc: BoCuc, x: number, y: number, boQua: readonly string[] = []): ViTri {
  const goc = { x: Math.round(so(x, LE_TRAI)), y: Math.round(so(y, LE_TREN)) };
  const khac = Object.entries(boCuc).filter(([id, p]) => !boQua.includes(id) && viTriHopLe(p)).map(([, p]) => p);
  const trong = (p: ViTri): boolean => khac.every((q) => !chamNhau(p, q, LE_TRONG));
  if (trong(goc)) return goc;
  const B = 24;
  for (let r = 1; r <= 80; r++) {
    let tot: ViTri | null = null, xa = Infinity;
    for (let dx = -r; dx <= r; dx++) for (let dy = -r; dy <= r; dy++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
      const p = { x: goc.x + dx * B, y: goc.y + dy * B };
      if (!trong(p)) continue;
      const d = dx * dx + dy * dy;
      if (d < xa) { xa = d; tot = p; }
    }
    if (tot) return tot;
  }
  const b = boundsCua(boCuc); // bố cục kín đặc quanh điểm đó (không thể xảy ra với ≤ 60 ô): đặt dưới cùng
  return { x: goc.x, y: Math.round(b ? b.y + b.h + KHOANG_DUONG - CAO_O : LE_TREN) };
}

/** Bù vị trí còn thiếu / hỏng (NaN, Infinity, sai kiểu) bằng vị trí tự dàn, dời đi nếu chỗ đó đã bị ô khác chiếm; giữ nguyên vị trí hợp lệ; bỏ vị trí của ô không còn. */
export function datViTriThieu(nut: readonly NutDoThi[], canh: readonly CanhDoThi[], boCuc: BoCuc | undefined): BoCuc {
  const ra: BoCuc = {};
  const cu = boCuc ?? {};
  for (const n of nut) { const p = cu[n.id]; if (viTriHopLe(p)) ra[n.id] = { x: p.x, y: p.y }; }
  if (Object.keys(ra).length === nut.length) return ra;
  const mac = tuDan(nut, canh);
  for (const n of nut) {
    if (ra[n.id]) continue;
    const m = mac[n.id] ?? { x: LE_TRAI, y: LE_TREN };
    ra[n.id] = viTriTrong(ra, m.x, m.y);
  }
  return ra;
}

/** Hộp bao (toạ độ thế giới) của các ô có vị trí hữu hạn; không có ô → null. */
export function boundsCua(boCuc: BoCuc, ids?: readonly string[]): { x: number; y: number; w: number; h: number } | null {
  const ds = (ids ?? Object.keys(boCuc)).map((i) => boCuc[i]).filter(viTriHopLe);
  if (!ds.length) return null;
  const x = Math.min(...ds.map((p) => p.x)), y = Math.min(...ds.map((p) => p.y));
  return { x, y, w: Math.max(...ds.map((p) => p.x)) + RONG_O - x, h: Math.max(...ds.map((p) => p.y)) + CAO_O - y };
}

/** Dời ô `id` thêm (dx, dy); bất biến, đầu vào NaN coi như 0. */
export function dichO(boCuc: BoCuc, id: string, dx: number, dy: number): BoCuc {
  const ra = sao(boCuc);
  const p = ra[id];
  if (p) ra[id] = { x: so(p.x) + so(dx), y: so(p.y) + so(dy) };
  return ra;
}

/** Các ô nằm SAU `id` trên cùng đường (kể cả `id` nếu `kemChinNo`). */
function phiaSau(canh: readonly CanhDoThi[], id: string, kemChinNo: boolean): string[] {
  const tiep = new Map(canh.map((c) => [c.tu, c.den] as const));
  const ra: string[] = [];
  const thay = new Set<string>();
  for (let cur: string | undefined = kemChinNo ? id : tiep.get(id); cur !== undefined && !thay.has(cur); cur = tiep.get(cur)) { thay.add(cur); ra.push(cur); }
  return ra;
}

// ================= THÊM / XOÁ Ô =================
export interface YeuCauThemO {
  /** Chèn vào giữa nối này (nối phải đang có). */
  truocCanh?: CanhDoThi;
  /** Nối ĐUÔI ô này; nếu ô này đã có ô kế tiếp → chèn vào giữa (vẫn một đường). */
  sauNutId?: string;
  /** Vị trí mong muốn (toạ độ thế giới, góc trên-trái); không có → tự chọn. */
  viTri?: ViTri;
}

/** Thêm `nutMoi` vào trạng thái (KHÔNG đổi `tt`): chèn giữa nối / nối đuôi / ô lẻ; tôn trọng trần 60 ô / 6 đường và mã ô không trùng; chọn vị trí không đè ô nào, đẩy các ô phía sau sang phải khi chèn mà chật. */
export function themO(tt: TrangThai, nutMoi: NutDoThi, yc: YeuCauThemO = {}): { tt: TrangThai } | { loi: string } {
  if (!nutMoi || !laId(nutMoi.id)) return { loi: "Ô mới không có mã." };
  if (tt.nut.some((n) => n.id === nutMoi.id)) return { loi: `Mã ô “${nutMoi.id}” đã có — mỗi ô một mã riêng.` };
  const ra: TrangThai = { nut: [...tt.nut.map((n) => ({ ...n, thamSo: { ...n.thamSo } })), { ...nutMoi, thamSo: { ...nutMoi.thamSo } }], canh: tt.canh.map((c) => ({ ...c })), boCuc: datViTriThieu(tt.nut, tt.canh, tt.boCuc) };
  let canhCu: CanhDoThi | undefined;
  let kieu: "chen" | "noi_duoi" | "co_lap" = "co_lap";
  let nguon: string | undefined;
  if (yc.truocCanh) {
    canhCu = tt.canh.find((c) => c.tu === yc.truocCanh!.tu && c.den === yc.truocCanh!.den);
    if (!canhCu) return { loi: "Không tìm thấy nối cần chèn vào giữa." };
    kieu = "chen";
  } else if (yc.sauNutId !== undefined) {
    if (!tt.nut.some((n) => n.id === yc.sauNutId)) return { loi: "Không tìm thấy ô để nối tiếp." };
    nguon = yc.sauNutId;
    const kế = tt.canh.find((c) => c.tu === nguon);
    if (kế) { canhCu = kế; kieu = "chen"; } else kieu = "noi_duoi";
  }
  const kiem = kiemThemO(tt, kieu);
  if (!kiem.ok) return { loi: kiem.lyDo };
  const bc = ra.boCuc;
  const id = nutMoi.id;
  if (canhCu) {
    const a = bc[canhCu.tu]!, b = bc[canhCu.den]!;
    const tuChon = !!yc.viTri && !yc.truocCanh; // người dùng thả ô ở một chỗ cụ thể → theo chỗ đó, không đẩy ô nào
    if (tuChon) bc[id] = viTriTrong(bc, so(yc.viTri!.x), so(yc.viTri!.y));
    else {
      const x = a.x + RONG_O + KHE_CHEN;
      const can = x + RONG_O + KHE_CHEN - b.x;
      if (can > 0) for (const sau of phiaSau(tt.canh, canhCu.tu, false)) bc[sau] = { x: bc[sau]!.x + can, y: bc[sau]!.y };
      bc[id] = viTriTrong(bc, Math.round(x), Math.round((a.y + bc[canhCu.den]!.y) / 2));
    }
    // cạnh cũ được thay bằng hai cạnh, giữ nguyên chỗ trong mảng
    const k = ra.canh.findIndex((c) => c.tu === canhCu!.tu && c.den === canhCu!.den);
    ra.canh.splice(k, 1, { tu: canhCu.tu, den: id }, { tu: id, den: canhCu.den });
  } else if (nguon !== undefined) {
    const s = bc[nguon]!;
    bc[id] = yc.viTri ? viTriTrong(bc, so(yc.viTri.x), so(yc.viTri.y)) : viTriTrong(bc, s.x + BUOC_NGANG, s.y);
    ra.canh.push({ tu: nguon, den: id });
  } else {
    const b = boundsCua(bc);
    const mac = b ? { x: b.x, y: b.y + b.h + (KHOANG_DUONG - CAO_O) } : { x: LE_TRAI, y: LE_TREN };
    bc[id] = yc.viTri ? viTriTrong(bc, so(yc.viTri.x), so(yc.viTri.y)) : viTriTrong(bc, mac.x, mac.y);
  }
  return { tt: ra };
}

/** Xoá ô `id`: gỡ mọi nối chạm ô; ô giữa đường thì TỰ NỐI LẠI hai đầu (`noiLai`). Ô không có → trạng thái không đổi. Bất biến. */
export function xoaO(tt: TrangThai, id: string): { tt: TrangThai; noiLai: CanhDoThi | null } {
  const ra: TrangThai = sao(tt);
  if (!ra.nut.some((n) => n.id === id)) return { tt: ra, noiLai: null };
  const truoc = ra.canh.find((c) => c.den === id), sau = ra.canh.find((c) => c.tu === id);
  ra.nut = ra.nut.filter((n) => n.id !== id);
  ra.canh = ra.canh.filter((c) => c.tu !== id && c.den !== id);
  delete ra.boCuc[id];
  if (ra.lap) ra.lap = ra.lap.filter((c) => c.tu !== id && c.den !== id); // vòng chạm ô bị xoá thì bỏ vòng
  let noiLai: CanhDoThi | null = null;
  if (truoc && sau && truoc.tu !== sau.den) { noiLai = { tu: truoc.tu, den: sau.den }; ra.canh.push(noiLai); }
  return { tt: ra, noiLai };
}

// ================= VIEW (pan / zoom) =================
export const clampK = (k: number): number => (hh(k) ? Math.min(K_MAX, Math.max(K_MIN, k)) : 1);
export function chuanHoaView(v: Partial<View> | undefined | null): View {
  return { k: hh(v?.k) ? clampK(v!.k!) : 1, tx: so(v?.tx), ty: so(v?.ty) };
}

/** Vừa khung: chứa trọn bố cục trong khung `vw × vh` (px màn hình), căn giữa, k ∈ [0,15; 1,2]. Khung 0 / âm / NaN (đang ẩn) → null: chưa làm gì được, KHÔNG sinh NaN. Không ô → {1, 36, 36}. */
export function vuaKhung(boCuc: BoCuc, vw: number, vh: number, le = 36): View | null {
  if (!hh(vw) || !hh(vh) || vw <= 0 || vh <= 0) return null;
  const b = boundsCua(boCuc);
  if (!b) return { k: 1, tx: le, ty: le };
  const k = clampK(Math.min(1.2, (vw - 2 * le) / b.w, (vh - 2 * le) / b.h));
  return { k, tx: (vw - b.w * k) / 2 - b.x * k, ty: (vh - b.h * k) / 2 - b.y * k };
}

/** Khung nhìn ĐẦU TIÊN trên màn hình hẹp (điện thoại): giữ chữ đọc được (k = 0,62) và neo góc trên-trái bố cục thay vì thu nhỏ cho vừa. Khung 0 / NaN → null. */
export function khungDauHep(boCuc: BoCuc, vw: number, vh: number, k = 0.62, le = 36): View | null {
  if (!hh(vw) || !hh(vh) || vw <= 0 || vh <= 0) return null;
  const b = boundsCua(boCuc);
  const kk = clampK(k);
  return b ? { k: kk, tx: le - b.x * kk, ty: le - b.y * kk } : { k: 1, tx: le, ty: le };
}

/** Zoom nhân hệ số `f` quanh điểm (px, py) tính từ góc khung. `f` không hợp lệ → không đổi. */
export function zoomQuanh(v: View, px: number, py: number, f: number): View {
  const c = chuanHoaView(v);
  if (!hh(f) || f <= 0) return c;
  const k2 = clampK(c.k * f), x = so(px), y = so(py);
  return { k: k2, tx: x - ((x - c.tx) * k2) / c.k, ty: y - ((y - c.ty) * k2) / c.k };
}

/** Chụm hai ngón: `dau` = lúc bắt đầu chụm (khoảng cách + trung điểm theo toạ độ trang), `nay` = hiện tại; `trai`/`tren` = góc khung. `v0` = view lúc bắt đầu. */
export function zoomChum(v0: View, dau: { d: number; cx: number; cy: number }, nay: { d: number; cx: number; cy: number }, trai: number, tren: number): View {
  const c = chuanHoaView(v0);
  const f = hh(dau.d) && dau.d > 0 && hh(nay.d) && nay.d > 0 ? nay.d / dau.d : 1;
  const k2 = clampK(c.k * f);
  const wx = (so(dau.cx) - so(trai) - c.tx) / c.k, wy = (so(dau.cy) - so(tren) - c.ty) / c.k;
  return { k: k2, tx: so(nay.cx) - so(trai) - wx * k2, ty: so(nay.cy) - so(tren) - wy * k2 };
}

/** Đường cong nối cổng phải ô `a` → cổng trái ô `b` (cạnh); `mx,my` = điểm giữa để đặt nút "+" và nhãn. Vị trí hỏng coi như 0. */
export function hinhHocCanh(a: ViTri, b: ViTri): { d: string; mx: number; my: number } {
  const x1 = so(a?.x) + RONG_O, y1 = so(a?.y) + CAO_O / 2, x2 = so(b?.x) - 8, y2 = so(b?.y) + CAO_O / 2;
  const dx = Math.max(50, Math.abs(x2 - x1) / 2);
  return { d: `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`, mx: (x1 + 3 * (x1 + dx) + 3 * (x2 - dx) + x2) / 8, my: (y1 + y2) / 2 };
}

/** Đường cong tạm khi đang kéo từ cổng ra tới điểm thế giới (wx, wy). */
export function hinhHocTam(a: ViTri, wx: number, wy: number): string {
  const x1 = so(a?.x) + RONG_O, y1 = so(a?.y) + CAO_O / 2, x = so(wx), y = so(wy), d = Math.max(40, Math.abs(x - x1) / 2);
  return `M${x1},${y1} C${x1 + d},${y1} ${x - d},${y} ${x},${y}`;
}

// ================= HOÀN TÁC / LÀM LẠI =================
/** Lịch sử hai chiều, bản chụp SÂU (đổi nguồn sau khi ghi không ảnh hưởng), trần 50 bản. `ghi(trước)` gọi TRƯỚC khi đổi; `hoanTac(hiệnTại)` trả bản trước (hoặc undefined). */
export class LichSu<T> {
  private truoc: T[] = [];
  private sau: T[] = [];
  constructor(private readonly tran = 50) {}
  ghi(s: T): void { this.truoc.push(sao(s)); if (this.truoc.length > this.tran) this.truoc.shift(); this.sau = []; }
  hoanTac(hienTai: T): T | undefined { const s = this.truoc.pop(); if (s === undefined) return undefined; this.sau.push(sao(hienTai)); return s; }
  lamLai(hienTai: T): T | undefined { const s = this.sau.pop(); if (s === undefined) return undefined; this.truoc.push(sao(hienTai)); return s; }
  /** Xem bản sẽ trả về mà KHÔNG lấy ra (để kiểm trước khi hoàn tác). */
  xemHoanTac(): T | undefined { const s = this.truoc[this.truoc.length - 1]; return s === undefined ? undefined : sao(s); }
  xemLamLai(): T | undefined { const s = this.sau[this.sau.length - 1]; return s === undefined ? undefined : sao(s); }
  get coHoanTac(): boolean { return this.truoc.length > 0; }
  get coLamLai(): boolean { return this.sau.length > 0; }
  get soHoanTac(): number { return this.truoc.length; }
  xoa(): void { this.truoc = []; this.sau = []; }
}

// ================= NHÃN MẠNG / CỜ / ĐỌC MÀN HÌNH =================
export interface NhanMang { ma: "fb" | "yt" | "tt" | "x" | "rd" | "kh"; chu: string; ten: string }
const MANG: Record<string, NhanMang> = {
  facebook: { ma: "fb", chu: "F", ten: "Facebook" },
  youtube: { ma: "yt", chu: "Y", ten: "YouTube" },
  tiktok: { ma: "tt", chu: "T", ten: "TikTok" },
  x: { ma: "x", chu: "X", ten: "X" },
  twitter: { ma: "x", chu: "X", ten: "X" },
  reddit: { ma: "rd", chu: "R", ten: "Reddit" },
};
/** Mã mạng ("facebook"…) → màu + chữ huy hiệu F/Y/T/X/R. Mạng lạ: chữ cái đầu, màu trung tính; rỗng: không huy hiệu. */
export function maMang(mang: string | undefined | null): NhanMang {
  const t = typeof mang === "string" ? mang.trim() : "";
  if (!t) return { ma: "kh", chu: "", ten: "" };
  return MANG[t.toLowerCase()] ?? { ma: "kh", chu: Array.from(t)[0]!.toUpperCase(), ten: t };
}

/** Mức cờ của một ô: có cờ đỏ → "do", chỉ vàng → "vang", không có → null. */
export function mucCo(cb: readonly CanhBaoNut[] | undefined | null): "vang" | "do" | null {
  if (!cb || !cb.length) return null;
  return cb.some((c) => c.muc === "do") ? "do" : "vang";
}

/** Nhãn đọc màn hình của một ô (aria-label): tên, mạng, tóm tắt, điểm nhường, cờ + lý do. Giữ nguyên chữ người dùng gõ. */
export function nhanAria(o: { ten: string; mang: NhanMang; tomTat?: string; nghi?: boolean; nhuong?: boolean; lan?: string | null; canhBao?: readonly CanhBaoNut[] }): string {
  const co = mucCo(o.canhBao);
  const p: string[] = [`${o.ten}${o.lan ? ` (${o.lan})` : ""}${o.mang.ten ? `, mạng ${o.mang.ten}` : ""}.`];
  if (o.tomTat) p.push(`${o.tomTat}.`);
  if (o.nghi && o.nhuong) p.push("Ô Nghỉ này là điểm nhường: bot làm xen đường khác trong lúc nghỉ.");
  if (co) p.push(co === "do" ? "Cờ đỏ — sẽ không chạy." : "Cờ vàng — nên xem lại.");
  for (const c of o.canhBao ?? []) p.push(`${c.muc === "do" ? "Lỗi" : "Cảnh báo"}: ${c.loi}`);
  return p.join(" ");
}

/** Thứ tự đọc cho danh sách dọc: từng đường, từng ô kèm số thứ tự toàn cục. */
export function thuTuDoc(nut: readonly NutDoThi[], canh: readonly CanhDoThi[]): { duong: number; thuTu: number; nut: NutDoThi }[] {
  let n = 0;
  return tachDuong(nut, canh).flatMap((d, i) => d.map((x) => ({ duong: i + 1, thuTu: ++n, nut: x })));
}

// ================= VÒNG LẶP "QUAY LẠI" (07/10/2026) =================
/** Vị trí (đường, chỉ số) của mỗi ô theo tachDuong. */
function viTriTrongDuong(nut: readonly NutDoThi[], canh: readonly CanhDoThi[]): Map<string, { d: number; i: number }> {
  const m = new Map<string, { d: number; i: number }>();
  tachDuong(nut, canh).forEach((ds, d) => ds.forEach((n, i) => m.set(n.id, { d, i })));
  return m;
}
/** Kéo từ `tu` về `den` đứng TRƯỚC nó trong CÙNG đường = người dùng muốn tạo vòng lặp (thay vì nối thường — nối thường sẽ thành vòng kín). */
export function laNoiNguoc(tt: Pick<TrangThai, "nut" | "canh">, tu: string, den: string): boolean {
  const v = viTriTrongDuong(tt.nut, tt.canh);
  const a = v.get(tu), b = v.get(den);
  return !!a && !!b && a.d === b.d && b.i < a.i;
}
/** Tạo vòng `tu` → `den` được không? (cùng luật với do-thi.ts kiemCauTrucLap — máy chủ kiểm lại, kể cả phần "tách đoạn an toàn" trang không biết). Không ném. */
export function kiemLap(tt: Pick<TrangThai, "nut" | "canh" | "lap">, tu: string, den: string, ten?: (id: string) => string): KetQuaKiem {
  const T = (id: string): string => `“${tenCuaId(ten, id)}”`;
  const v = viTriTrongDuong(tt.nut, tt.canh);
  const a = v.get(tu), b = v.get(den);
  if (!laId(tu) || !laId(den) || !a || !b) return { ok: false, ma: "khong_co_o", lyDo: "Không tìm thấy ô để tạo vòng lặp (có thể ô vừa bị xoá)." };
  if (tu === den) return { ok: false, ma: "lap_chinh_no", lyDo: `Vòng lặp phải quay về một ô ĐỨNG TRƯỚC — không quay về chính nó (ô ${T(tu)})` };
  if (a.d !== b.d) return { ok: false, ma: "khac_duong", lyDo: `Ô ${T(den)} ở đường khác — vòng lặp chỉ quay về một ô đứng trước trong CÙNG đường với ${T(tu)}.` };
  if (b.i > a.i) return { ok: false, ma: "ve_o_sau", lyDo: `Ô ${T(den)} đứng SAU ${T(tu)} — vòng lặp chỉ quay về ô đứng trước (muốn làm tiếp thì nối thường).` };
  const lap = (tt.lap ?? []).filter((c) => c.tu !== tu || c.den !== den);
  const cu = lap.find((c) => c.tu === tu);
  if (cu) return { ok: false, ma: "da_co_lap", lyDo: `Ô ${T(tu)} đã có vòng quay lại ${T(cu.den)} — mỗi ô chỉ một vòng đi ra. Bấm nhãn “lặp ×${cu.soLan}” để sửa hoặc gỡ vòng đó.` };
  for (const c of lap) {
    const p = v.get(c.tu), q = v.get(c.den);
    if (!p || !q || p.d !== a.d) continue;
    const [m, n] = q.i <= b.i ? [{ a: q.i, b: p.i }, { a: b.i, b: a.i }] : [{ a: b.i, b: a.i }, { a: q.i, b: p.i }];
    if (m.a < n.a && n.a <= m.b && m.b < n.b) return { ok: false, ma: "cat_cheo", lyDo: `Vòng này cắt chéo vòng ${T(c.tu)} → ${T(c.den)} — các vòng phải lồng nhau hoặc nối tiếp nhau.` };
  }
  return { ok: true };
}
/** Chuẩn hoá vòng do bên ngoài đưa vào (giữ vòng hợp lệ ĐẦU TIÊN theo kiemLap, soLan nguyên 1..20). `boQua` = số vòng bị bỏ. Không ném. */
export function chuanHoaLap(nut: readonly NutDoThi[], canh: readonly CanhDoThi[], lap: readonly CanhLap[] | undefined): { lap: CanhLap[]; boQua: number } {
  const ra: CanhLap[] = [];
  let boQua = 0;
  for (const c of Array.isArray(lap) ? lap : []) {
    const ok = !!c && typeof c === "object" && Number.isInteger(c.soLan) && c.soLan >= GIOI_HAN_LAP.soLanToiThieu && c.soLan <= GIOI_HAN_LAP.soLanToiDa && kiemLap({ nut: [...nut], canh: [...canh], lap: ra }, c.tu, c.den).ok;
    if (!ok) { boQua++; continue; }
    ra.push({ tu: c.tu, den: c.den, kieu: "lap", soLan: c.soLan, ...(typeof c.denGio === "string" && c.denGio ? { denGio: c.denGio } : {}), ...(c.denHetNghi === true ? { denHetNghi: true } : {}) });
  }
  return { lap: ra, boQua };
}
/** Cạnh lặp vẽ CONG PHÍA DƯỚI: từ đáy ô `tu` (a) về đáy ô `den` (b); `bac` = vòng lồng thứ mấy (càng ngoài càng sâu). `mx,my` = chỗ đặt nhãn "lặp ×2". Số hỏng coi như 0. */
export function hinhHocLap(a: ViTri, b: ViTri, bac = 0): { d: string; mx: number; my: number } {
  // đầu / cuối ở GẦN GÓC đáy ô (giữa đáy là chữ ước lượng thời gian + nhãn "điểm nhường"; góc dưới-trái là cờ)
  const x1 = so(a?.x) + RONG_O - 20, y1 = so(a?.y) + CAO_O, x2 = so(b?.x) + 24, y2 = so(b?.y) + CAO_O + 6;
  const sau = 74 + Math.max(0, so(bac)) * 22; // nhãn "lặp ×n" nằm dưới chữ ước lượng + nhãn "điểm nhường"
  const day = Math.max(y1, y2) + sau;
  return { d: `M${x1},${y1} C${x1},${day} ${x2},${day} ${x2},${y2}`, mx: (x1 + x2) / 2, my: Math.max(y1, y2) + sau * 0.75 };
}
/** Nhãn của cạnh lặp: "lặp ×2" / "lặp ×5 · tới 17:30". */
export const nhanLap = (c: Pick<CanhLap, "soLan" | "denGio" | "denHetNghi">): string => (c.denHetNghi ? `lặp tới khi hết bước Nghỉ${c.denGio ? ` · tới ${c.denGio}` : ""}` : `lặp ×${c.soLan}${c.denGio ? ` · tới ${c.denGio}` : ""}`);
/** (đợt A) Các NHÁNH khác nhánh của bước `id` (cho ô chọn "Dành cho" của bước Nghỉ): mã bước đầu nhánh + các bước theo thứ tự chạy. */
export function cacNhanhKhac(nut: readonly NutDoThi[], canh: readonly CanhDoThi[], id: string): { id: string; nut: NutDoThi[] }[] {
  const ds = tachDuong(nut, canh);
  const cua = ds.find((d) => d.some((x) => x.id === id));
  if (!cua) return [];
  return ds.filter((d) => d !== cua && d.length).map((d) => ({ id: d[0]!.id, nut: [...d] }));
}
/** (đợt A) Chữ tần suất của một bước: "chỉ lần đầu mỗi ngày" / "cứ 3 ngày một lần" / "". */
export function chuTanSuat(t: NutDoThi["tanSuat"] | null | undefined): string {
  if (!t) return "";
  return t.kieu === "dau_ngay" ? "chỉ lần đầu mỗi ngày" : `cứ ${t.n} ngày một lần`;
}
/** Bậc lồng của mỗi vòng (0 = trong cùng) để vẽ cạnh lặp ngoài sâu hơn cạnh trong. */
export function bacLap(nut: readonly NutDoThi[], canh: readonly CanhDoThi[], lap: readonly CanhLap[]): Map<string, number> {
  const v = viTriTrongDuong(nut, canh);
  const ra = new Map<string, number>();
  for (const c of lap) {
    const p = v.get(c.tu), q = v.get(c.den);
    let bac = 0;
    if (p && q) for (const k of lap) { if (k === c) continue; const x = v.get(k.tu), y = v.get(k.den); if (x && y && x.d === p.d && y.i >= q.i && x.i <= p.i && (y.i > q.i || x.i < p.i)) bac++; }
    ra.set(`${c.tu}>${c.den}`, bac);
  }
  return ra;
}

// ================= THỜI GIAN ƯỚC CHỪNG (chữ) =================
const chuMotMuc = (g: number): string => {
  const t = Math.round(g);
  if (t < 60) return `${t} giây`;
  if (t < 3600) return `${Math.round(t / 60)} phút`;
  const gio = Math.floor(t / 3600), p = Math.round((t % 3600) / 60);
  return p ? `${gio} giờ ${p} phút` : `${gio} giờ`;
};
/** "~45 giây" / "~3 phút" / khoảng "1–3 phút" (từ ≠ đến) / "×3" khi ô nằm trong vòng lặp. Đầu vào hỏng → "". */
export function chuUoc(u: { tuS: number; denS: number } | null | undefined, nhanVong = 1): string {
  if (!u || !hh(u.tuS) || !hh(u.denS) || u.denS < 0) return "";
  const tu = Math.max(0, Math.min(u.tuS, u.denS)), den = Math.max(u.tuS, u.denS);
  let chu: string;
  if (Math.round(tu) === Math.round(den)) chu = `~${chuMotMuc(den)}`;
  else if (den < 120) chu = `${Math.round(tu)}–${Math.round(den)} giây`;
  else if (den <= 3600) chu = `${Math.round(tu / 60)}–${Math.round(den / 60)} phút`;
  else chu = `${chuMotMuc(tu)}–${chuMotMuc(den)}`;
  return nhanVong > 1 ? `${chu} ×${nhanVong}` : chu;
}

/** Hệ số nhân theo ô = tích (số lần lặp thêm + 1) của mọi vòng chứa ô (ước lượng "~45 giây ×3"). */
export function heSoVong(nut: readonly NutDoThi[], canh: readonly CanhDoThi[], lap: readonly CanhLap[] | undefined): Map<string, number> {
  const ra = new Map<string, number>(nut.map((n) => [n.id, 1] as const));
  for (const ds of tachDuong(nut, canh)) {
    const idx = new Map(ds.map((n, i) => [n.id, i] as const));
    for (const c of lap ?? []) {
      const a = idx.get(c.den), b = idx.get(c.tu);
      if (a === undefined || b === undefined || a > b || !hh(c.soLan) || c.denHetNghi) continue; // (đợt A) lặp tới khi hết bước Nghỉ: số vòng do thời gian nghỉ quyết — không nhân
      for (let i = a; i <= b; i++) ra.set(ds[i]!.id, (ra.get(ds[i]!.id) ?? 1) * (c.soLan + 1));
    }
  }
  return ra;
}
/** "Lặp cả sơ đồ ×3, nghỉ 30–60 phút giữa vòng" / "…, không nghỉ giữa vòng"; không lặp → "". */
export function chuLapCaSoDo(lc: { soVong: number; nghiGiuaVongTuS: number; nghiGiuaVongDenS: number; denGio?: string } | null | undefined): string {
  if (!lc) return "";
  const nghi = lc.nghiGiuaVongDenS > 0 ? `nghỉ ${chuUoc({ tuS: lc.nghiGiuaVongTuS, denS: lc.nghiGiuaVongDenS }).replace(/^~/, "")} giữa vòng` : "không nghỉ giữa vòng";
  return `Lặp cả sơ đồ ×${lc.soVong}, ${nghi}${lc.denGio ? `, tới ${lc.denGio}` : ""}`;
}
/** Ô số lần lặp: chữ người gõ → số nguyên 1..20, hoặc lý do tiếng Việt. */
export function docSoLan(v: string): { ok: true; so: number } | { ok: false; lyDo: string } {
  const t = (v ?? "").trim();
  if (!/^\d+$/.test(t)) return { ok: false, lyDo: `Số lần phải là số nguyên từ ${GIOI_HAN_LAP.soLanToiThieu} đến ${GIOI_HAN_LAP.soLanToiDa}` };
  const so = Number(t);
  if (so < GIOI_HAN_LAP.soLanToiThieu || so > GIOI_HAN_LAP.soLanToiDa) return { ok: false, lyDo: `Số lần phải từ ${GIOI_HAN_LAP.soLanToiThieu} đến ${GIOI_HAN_LAP.soLanToiDa} (không bao giờ lặp mãi)` };
  return { ok: true, so };
}
